import { useState } from 'react';
import type { Vessel, TankReading } from '../types';
import { calculateTankWeight } from '../lib/calculations';
import { ArrowRight, Droplets } from 'lucide-react';

interface UllageInputProps {
  vessel: Vessel;
  onCalculate: (result: {
    totalWeight: number;
    totalGSV: number;
    details: string[];
    warnings: string[];
    tank_readings: TankReading[];
  }) => void;
}

export default function UllageInput({ vessel, onCalculate }: UllageInputProps) {
  const [readings, setReadings] = useState<TankReading[]>(
    vessel.tank_tables?.map((t) => ({
      tank_no: t.tank_no,
      ullage: 0,
      temp: 15,
      observed_density: 0.85,
    })) || []
  );
  

  const update = (i: number, field: keyof TankReading, val: string) => {
    const num = val === '' ? 0 : parseFloat(val);
    setReadings((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: num } : r)));
  };

  const handleCalc = () => {
    const result = calculateTankWeight(readings, vessel);
    onCalculate({ ...result, tank_readings: result.updated });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="glass rounded-xl p-4 flex items-center gap-3">
        <Droplets className="w-5 h-5 text-emerald-400" />
        <div className="text-sm">
          <span className="text-navy-300 font-medium">{vessel.name}</span>
          <span className="text-navy-500 mx-2">•</span>
          <span className="text-navy-400">{vessel.tank_tables?.length || 0} tanks</span>
        </div>
      </div>

      {/* Mobile-friendly card layout */}
      <div className="space-y-3">
        {readings.map((r, i) => (
          <div key={r.tank_no} className="card-premium p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-bold text-navy-100 text-sm">{r.tank_no}</span>
              <span className="text-xs text-navy-500">
                {vessel.tank_tables?.find(t => t.tank_no === r.tank_no)?.name || ''}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Ullage <span className="text-navy-600">(m)</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="input-premium py-2 text-sm"
                  value={r.ullage || ''}
                  onChange={(e) => update(i, 'ullage', e.target.value)}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Temp <span className="text-navy-600">(°C)</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="input-premium py-2 text-sm"
                  value={r.temp || ''}
                  onChange={(e) => update(i, 'temp', e.target.value)}
                  placeholder="15.0"
                />
              </div>
              <div>
                <label className="block text-[10px] text-navy-500 mb-1">
                  Density <span className="text-navy-600">(t/m³)</span>
                </label>
                <input
                  type="number"
                  step="0.001"
                  className="input-premium py-2 text-sm"
                  value={r.observed_density || ''}
                  onChange={(e) => update(i, 'observed_density', e.target.value)}
                  placeholder="0.850"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={handleCalc} className="btn-primary w-full flex items-center justify-center gap-2">
        Calculate Cargo Weight <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
